"use client";

import { createContext, useContext, useEffect, useState } from "react";
import { type DemoUser, type AppRole, type OperationalRole, type Department, type Capability, type AuthorityLevel, type FunctionalGroup, TENANT_USERS, DEMO_USERS } from "@/lib/auth/permissions";
import { parseTenantId, TENANTS } from "@/lib/tenants";
import { getManagedAccounts, toDemoUser } from "@/lib/accounts-store";
import { signIn as supabaseSignIn, signOut as supabaseSignOut, getAuthUser } from "@/lib/auth/actions";
import { isLiveTenant } from "@/lib/live-tenant";

interface AuthContextValue {
  user: DemoUser | null;
  role: AppRole | null;
  department: Department | null;
  capabilities: Capability[];
  authorityLevel: AuthorityLevel;
  functionalGroups: FunctionalGroup[];
  operationalRoles: OperationalRole[];
  tenantId: string;
  /** Sign in with email + password. Returns { error } on failure, null error on success. */
  login: (email: string, password: string) => Promise<{ error: string | null }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  role: null,
  department: null,
  capabilities: [],
  authorityLevel: "none",
  functionalGroups: [],
  operationalRoles: [],
  tenantId: "demo",
  login: async () => ({ error: "Not initialised" }),
  logout: async () => {},
});

function getStorageKey(tenantId: string) {
  return `opscom_user_${tenantId}`;
}

/** Returns true when Supabase env vars are real (not placeholder). */
function isSupabaseConfigured(): boolean {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL ?? "";
  return url.startsWith("https://") && !url.includes("your-supabase");
}

function getCurrentTenantId(): string {
  if (typeof window === "undefined") return "demo";
  const fromCookie = parseTenantId(document.cookie);
  if (fromCookie !== "demo" || document.cookie.includes("tenant-id=demo")) {
    return fromCookie;
  }
  const host = window.location.hostname;
  if (!host.includes("localhost") && !host.includes(".vercel.app")) {
    const parts = host.split(".");
    const sub = parts[0];
    if (sub === "www" || parts.length === 2) return "www";
    if (sub === "account" || sub === "portal") return sub;
    if (TENANTS[sub]) return sub;
  }
  return "demo";
}

function getTenantUsers(tenantId: string): DemoUser[] {
  const seeded = TENANT_USERS[tenantId] ?? DEMO_USERS;
  const managed = getManagedAccounts(tenantId).map(toDemoUser);
  const managedEmails = new Set(managed.map((u) => u.email.toLowerCase()));
  const filtered = seeded.filter((u) => !managedEmails.has(u.email.toLowerCase()));
  return [...filtered, ...managed];
}

/** Map a Supabase profile row to a DemoUser for backwards compatibility. */
function profileToDemoUser(profile: {
  id: string;
  full_name: string;
  email: string;
  role: string;
  operational_roles?: string[];
  avatar_color?: string | null;
  tenant_id: string;
  // New enterprise architecture fields (optional — safe before migration)
  department?: string | null;
  capabilities?: string[];
  authority_level?: string | null;
  functional_groups?: string[];
}): DemoUser {
  const name = profile.full_name ?? "";
  const initials = name.split(/\s+/).map(p => p[0] ?? "").join("").slice(0, 3).toUpperCase() || "U";
  return {
    id: profile.id,
    name,
    initials,
    description: profile.role,
    email: profile.email,
    password: "",          // never stored / exposed client-side
    role: profile.role as AppRole,
    department: (profile.department as Department) ?? undefined,
    capabilities: (profile.capabilities ?? []) as Capability[],
    authorityLevel: (profile.authority_level as AuthorityLevel) ?? "none",
    functionalGroups: (profile.functional_groups ?? []) as FunctionalGroup[],
    operationalRoles: (profile.operational_roles ?? []) as OperationalRole[],
    avatarColor: profile.avatar_color ?? "from-blue-500 to-blue-700",
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<DemoUser | null>(null);
  const [tenantId, setTenantId] = useState<string>("demo");

  useEffect(() => {
    const tid = getCurrentTenantId();
    setTenantId(tid);

    if (isLiveTenant(tid) && isSupabaseConfigured()) {
      // ── Supabase auth path (via server action — no browser SDK, no navigator locks) ──
      let cancelled = false;
      getAuthUser().then((profile) => {
        if (!cancelled && profile) setUser(profileToDemoUser(profile));
      }).catch(() => {});
      return () => { cancelled = true; };
    } else {
      // ── Client-side auth path (demo tenant) ───────────────────────
      try {
        const stored = localStorage.getItem(getStorageKey(tid));
        if (stored) {
          const parsed = JSON.parse(stored) as DemoUser;
          const users = getTenantUsers(tid);
          const valid = users.find((u) => u.id === parsed.id);
          if (valid) setUser(valid);
        }
      } catch { /* ignore */ }
    }
  }, []);

  async function login(email: string, password: string): Promise<{ error: string | null }> {
    if (isLiveTenant(tenantId) && isSupabaseConfigured()) {
      // ── Supabase sign-in via server action (no browser SDK, no navigator locks) ──
      try {
        const result = await supabaseSignIn(email, password);
        if (result.error) return { error: result.error };
        // Fetch the profile now that the session cookie is set
        const profile = await getAuthUser();
        if (profile) setUser(profileToDemoUser(profile));
        return { error: null };
      } catch (err: any) {
        return { error: err?.message ?? "Sign in failed. Please try again." };
      }
    } else {
      // ── Client-side check (demo) ──────────────────────────────────
      await new Promise((r) => setTimeout(r, 600));
      const users = getTenantUsers(tenantId);
      const match = users.find(
        (u) => u.email.toLowerCase() === email.toLowerCase() && u.password === password
      );
      if (!match) return { error: "Invalid email or password." };
      setUser(match);
      try { localStorage.setItem(getStorageKey(tenantId), JSON.stringify(match)); } catch { /* ignore */ }
      return { error: null };
    }
  }

  async function logout(): Promise<void> {
    setUser(null);
    if (isLiveTenant(tenantId) && isSupabaseConfigured()) {
      try { await supabaseSignOut(); } catch { /* ignore */ }
    } else {
      try { localStorage.removeItem(getStorageKey(tenantId)); } catch { /* ignore */ }
    }
  }

  return (
    <AuthContext.Provider value={{
      user,
      role: user?.role ?? null,
      department: user?.department ?? null,
      capabilities: user?.capabilities ?? [],
      authorityLevel: user?.authorityLevel ?? "none",
      functionalGroups: user?.functionalGroups ?? [],
      operationalRoles: user?.operationalRoles ?? [],
      tenantId,
      login,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
